/**
 * The applicant's personal room link (owner request 2026-11-01). Before its time: the booked time, a
 * countdown, what to prepare, and «لا أستطيع الحضور» (up to two hours before). From ten minutes before:
 * the audio call with whoever of the administration joins.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarX2, Check, Mic, MicOff, PhoneOff, RotateCcw } from "lucide-react";
import type { CancelInterviewResponse, InterviewRoomResponse } from "@mubin/contracts";
import { errorText } from "../lib/api";
import { useInterviewCall } from "../lib/interview-call";
import { ar, clock, hijriDate, initial, linkToken, weekday, withToken } from "../lib/interview";
import { Closed, Shell } from "./interview-book";

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function InterviewRoomPage() {
  const token = useMemo(linkToken, []);
  const justBooked = useMemo(() => new URLSearchParams(window.location.hash.slice(1)).has("booked"), []);
  const room = useQuery({
    queryKey: ["interview-room", token],
    queryFn: () => withToken<InterviewRoomResponse>("/v1/public/interview/room", token!),
    enabled: !!token,
    retry: false,
    refetchInterval: 20_000,
  });
  const now = useNow();
  if (!token || room.isError) return <Closed />;
  if (room.isPending) return <Shell loading />;
  const r = room.data;
  const open = r.opensAt && r.closesAt && now >= Date.parse(r.opensAt) && now <= Date.parse(r.closesAt);
  if (r.status === "booked" && open) return <Call room={r} token={token} />;
  if (r.status === "booked" && r.opensAt && now < Date.parse(r.opensAt))
    return (
      <Booked room={r} token={token} justBooked={justBooked} now={now} onChange={() => void room.refetch()} />
    );
  return <Over room={r} />;
}

function Booked({
  room: r,
  token,
  justBooked,
  now,
  onChange,
}: {
  room: InterviewRoomResponse;
  token: string;
  justBooked: boolean;
  now: number;
  onChange: () => void;
}) {
  const tz = r.timezone;
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rebook, setRebook] = useState<string | null | undefined>(undefined);
  const left = Math.max(0, Date.parse(r.startsAt!) - now);
  const d = Math.floor(left / 86_400_000);
  const h = Math.floor((left % 86_400_000) / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);

  async function cancel() {
    setBusy(true);
    setError(null);
    try {
      const res = await withToken<CancelInterviewResponse>("/v1/public/interview/room/cancel", token, {
        method: "POST",
      });
      setRebook(res.bookingToken);
    } catch (e) {
      setError(errorText(e));
      onChange();
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  if (rebook !== undefined)
    return (
      <Shell>
        <header className="iv-top">
          <img src="/logo-ivory.png" alt="مُبين" />
        </header>
        <div className="iv-body iv-center">
          <div className="iv-check muted">
            <div>
              <CalendarX2 size={28} />
            </div>
          </div>
          <div className="iv-title">تم إلغاء موعد مقابلتك</div>
          {rebook ? (
            <>
              <p className="iv-sub">
                تقدر تختار موعدًا جديدًا الآن (مرة واحدة)، وأرسلنا لك الرابط على الواتساب.
              </p>
              <div className="iv-cta">
                <button type="button" onClick={() => window.location.replace(`/interview/book#t=${rebook}`)}>
                  اختيار موعد جديد
                </button>
              </div>
            </>
          ) : (
            <p className="iv-sub">إن رغبت في موعد آخر، تواصل مع الجمعية.</p>
          )}
        </div>
      </Shell>
    );

  return (
    <Shell>
      <header className="iv-top" style={{ paddingBottom: 18 }}>
        <img src="/logo-ivory.png" alt="مُبين" />
      </header>
      <div className="iv-body iv-center" style={{ paddingBottom: 40 }}>
        <div className="iv-check">
          <div>
            <Check size={30} strokeWidth={3} />
          </div>
        </div>
        <div className="iv-title">{justBooked ? "تم تأكيد موعد مقابلتك" : "موعد مقابلتك"}</div>
        <p className="iv-sub">
          {justBooked ? "أرسلنا لك التفاصيل على الواتساب" : "احفظ هذا الرابط، فمنه تدخل المقابلة"}
        </p>
        {error && (
          <div className="iv-error" role="alert" style={{ marginTop: 12 }}>
            {error}
          </div>
        )}
        <div className="iv-card">
          <div className="iv-row">
            <span>المتقدم</span>
            <b>{r.name}</b>
          </div>
          <div className="iv-row">
            <span>اليوم</span>
            <b>{hijriDate(r.startsAt!, tz)}</b>
          </div>
          <div className="iv-row">
            <span>الوقت</span>
            <b>{clock(r.startsAt!, tz)} بتوقيت السعودية</b>
          </div>
          <div className="iv-row">
            <span>الطريقة</span>
            <b>مقابلة صوتية عبر الإنترنت</b>
          </div>
        </div>
        <div className="iv-label">موعد مقابلتك بعد</div>
        <div className="iv-countdown" aria-live="polite">
          {d > 0 && (
            <div>
              <b>{ar(d)}</b>يوم
            </div>
          )}
          <div>
            <b>{ar(h)}</b>ساعة
          </div>
          <div>
            <b>{ar(m)}</b>دقيقة
          </div>
        </div>
        <div className="iv-card iv-tips">
          🎧 جهّز سماعة أو مكانًا هادئًا
          <br />
          🎙️ اسمح للمتصفح باستخدام الميكروفون
          <br />
          🕐 يفتح رابط الدخول قبل الموعد بعشر دقائق
        </div>
        {r.canCancel &&
          (confirming ? (
            <>
              <p className="iv-sub">
                {r.canRebook
                  ? "متأكد؟ يُلغى الموعد، وتختار موعدًا جديدًا مرة واحدة."
                  : "متأكد؟ يُلغى الموعد، ولا يمكن اختيار موعد آخر من الرابط."}
              </p>
              <button type="button" className="iv-ghost" disabled={busy} onClick={() => void cancel()}>
                {busy ? "جارٍ الإلغاء…" : "نعم، ألغِ الموعد"}
              </button>
              <button type="button" className="iv-ghost" onClick={() => setConfirming(false)}>
                تراجع
              </button>
            </>
          ) : (
            <button type="button" className="iv-ghost" onClick={() => setConfirming(true)}>
              لا أستطيع الحضور، ألغِ الموعد
            </button>
          ))}
      </div>
    </Shell>
  );
}

function Over({ room: r }: { room: InterviewRoomResponse }) {
  const text =
    r.status === "finished"
      ? { title: "انتهت المقابلة", sub: "جزاك الله خيرًا، تصلك نتيجة طلبك من الجمعية بإذن الله." }
      : r.status === "cancelled"
        ? { title: "أُلغي هذا الموعد", sub: "إن رغبت في موعد آخر، تواصل مع الجمعية." }
        : { title: "انتهى وقت المقابلة", sub: "إن فاتك الموعد، تواصل مع الجمعية لموعد آخر." };
  return (
    <Shell>
      <header className="iv-top">
        <img src="/logo-ivory.png" alt="مُبين" />
      </header>
      <div className="iv-body iv-center">
        <div className={`iv-check${r.status === "finished" ? "" : " muted"}`}>
          <div>
            {r.status === "finished" ? <Check size={30} strokeWidth={3} /> : <CalendarX2 size={28} />}
          </div>
        </div>
        <div className="iv-title">{text.title}</div>
        <p className="iv-sub">{text.sub}</p>
      </div>
    </Shell>
  );
}

function Call({ room: r, token }: { room: InterviewRoomResponse; token: string }) {
  const [left, setLeft] = useState(false);
  const [muted, setMuted] = useState(false);
  const call = useInterviewCall({
    enabled: !left,
    side: "applicant",
    interviewId: r.interviewId,
    auth: () => ({ type: "auth", applicantToken: token }),
    iceServers: () => withToken("/v1/public/interview/room/ice-servers", token),
  });
  const now = useNow();
  const [since, setSince] = useState<number | null>(null);
  const talking = call.peer && call.call?.phase === "connected";
  useEffect(() => {
    if (talking && since === null) setSince(Date.now());
    if (!call.peer) setSince(null);
  }, [talking, call.peer, since]);
  const tz = r.timezone;
  const micError = call.call?.error === "mic_denied" || call.call?.error === "mic_unavailable";
  const status = left
    ? "خرجت من المقابلة"
    : call.closed === "replaced"
      ? "فُتحت المقابلة في نافذة أخرى"
      : call.closed === "closed"
        ? "المقابلة غير متاحة الآن"
        : call.socket !== "ready"
          ? "جارٍ الاتصال…"
          : micError
            ? "الميكروفون غير متاح"
            : !call.peer
              ? "في انتظار انضمام الإدارة…"
              : call.call?.phase === "reconnecting"
                ? "انقطع الصوت، نعيد الربط…"
                : talking
                  ? "متصل الآن مع الإدارة"
                  : "جارٍ ربط الصوت…";
  const seconds = since ? Math.floor((now - since) / 1000) : 0;

  return (
    <main className="iv-page" dir="rtl">
      <div className="iv-sheet">
        <section className="iv-room" aria-live="polite">
          <img src="/logo-ivory.png" alt="مُبين" />
          <div className={`iv-avatar${talking ? " live" : ""}`} aria-hidden="true">
            {initial(r.name)}
          </div>
          <h2>{r.name}</h2>
          <div className="iv-when">
            مقابلة التطوع · {weekday(r.startsAt!, tz)} {clock(r.startsAt!, tz)}
          </div>
          <div className="iv-status">
            <span className={`iv-dot${talking ? " ok" : ""}`} />
            {status}
            {talking && (
              <>
                {" · "}
                <bdi dir="ltr">
                  {ar(Math.floor(seconds / 60)).padStart(2, "٠")}:{ar(seconds % 60).padStart(2, "٠")}
                </bdi>
              </>
            )}
          </div>
          {/* The bars move while the two sides are connected. */}
          <div className={`iv-wave${talking ? " live" : ""}`} aria-hidden="true">
            {[8, 14, 22, 30, 18, 26, 36, 24, 14, 20, 30, 16, 10, 6].map((h, i) => (
              <i
                key={i}
                style={{
                  height: `${talking ? h : Math.max(4, h * 0.4)}px`,
                  animationDelay: `${(i % 5) * 0.12}s`,
                }}
              />
            ))}
          </div>
          {!left && !call.closed && (
            <div className="iv-room-tips">
              {micError ? (
                <span className="bad">
                  ✕ اسمح للمتصفح باستخدام الميكروفون من إعدادات الموقع، ثم أعد تحميل الصفحة
                </span>
              ) : (
                <>✓ {call.call && call.call.phase !== "failed" ? "الميكروفون جاهز" : "نجهّز الميكروفون…"}</>
              )}
              <br />
              {call.call?.quality === "poor" ? (
                <span className="bad">✕ الاتصال ضعيف، اقترب من الشبكة إن أمكن</span>
              ) : (
                <>✓ {call.socket === "ready" ? "الاتصال جيد" : "نتصل بالخادم…"}</>
              )}
              <br />
              {talking
                ? "تحدث بشكل طبيعي، والمقابلة لا تُسجَّل"
                : "ستبدأ المقابلة عند دخول الإدارة، ابقَ في هذه الصفحة"}
            </div>
          )}
          {(left || call.closed === "replaced") && (
            <button type="button" className="iv-gold-btn" onClick={() => window.location.reload()}>
              <RotateCcw size={16} /> العودة للمقابلة
            </button>
          )}
          {!left && !call.closed && (
            <div className="iv-controls">
              <button
                type="button"
                className="iv-round"
                aria-pressed={muted}
                aria-label={muted ? "تشغيل الميكروفون" : "كتم الميكروفون"}
                onClick={() => {
                  call.setMuted(!muted);
                  setMuted(!muted);
                }}
              >
                {muted ? <MicOff size={26} /> : <Mic size={26} />}
              </button>
              <button
                type="button"
                className="iv-round end"
                aria-label="الخروج من المقابلة"
                onClick={() => {
                  call.leave();
                  setLeft(true);
                }}
              >
                <PhoneOff size={26} />
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
